// src/components/site-editor/panels/ColorSchemePanel.js
import React, { useState } from 'react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import ColorPicker from '../../editors/ColorPicker';
import ColorPaletteHelper from '../../editors/ColorPaletteHelper';
import { getTextColor, getSecondaryTextColor } from '../../../utils/colorUtils';
import { Check } from 'lucide-react';

// Background Mode Swatches (Curated)
const BACKGROUND_SWATCHES = {
  light: [
    { id: 'pure-white', color: '#FFFFFF', name: 'Pure White' },
    { id: 'soft-white', color: '#FAFAFA', name: 'Soft White' },
    { id: 'warm-cream', color: '#FAF7F2', name: 'Warm Cream' },
    { id: 'light-gray', color: '#F5F5F5', name: 'Light Gray' },
    { id: 'cool-white', color: '#F8F9FA', name: 'Cool White' }
  ],
  dark: [
    { id: 'medium-charcoal', color: '#374151', name: 'Medium Charcoal' },
    { id: 'slate', color: '#2D3748', name: 'Slate' },
    { id: 'dark-charcoal', color: '#1F2937', name: 'Dark Charcoal' },
    { id: 'near-black', color: '#111827', name: 'Near Black' },
    { id: 'pure-black', color: '#000000', name: 'Pure Black' }
  ]
};

const COLOR_PRESETS = [
  // Light & Airy
  {
    id: 'lavender-gold',
    name: 'Lavender & Gold',
    category: 'light',
    backgroundSwatch: 'pure-white',
    colors: {
      primary: '#7C3AED',
      secondary: '#FCD34D',
      background: '#FFFFFF',
      text: '#1F2937',
      accent: '#EC4899'
    }
  },
  {
    id: 'sage-rose',
    name: 'Sage & Rose',
    category: 'light',
    backgroundSwatch: 'warm-cream',
    colors: {
      primary: '#10B981',
      secondary: '#F87171',
      background: '#FAF7F2',
      text: '#1F2937',
      accent: '#A78BFA'
    }
  },
  {
    id: 'ocean-coral',
    name: 'Ocean & Coral',
    category: 'light',
    backgroundSwatch: 'pure-white',
    colors: {
      primary: '#0EA5E9',
      secondary: '#FB923C',
      background: '#FFFFFF',
      text: '#1F2937',
      accent: '#14B8A6'
    }
  },
  {
    id: 'rose-quartz',
    name: 'Rose Quartz',
    category: 'light',
    backgroundSwatch: 'warm-cream',
    colors: {
      primary: '#BE185D',
      secondary: '#F9A8D4',
      background: '#FAF7F2',
      text: '#1F2937',
      accent: '#FECDD3'
    }
  },
  {
    id: 'slate-silver',
    name: 'Slate & Silver',
    category: 'light',
    backgroundSwatch: 'cool-white',
    colors: {
      primary: '#475569',
      secondary: '#94A3B8',
      background: '#F8F9FA',
      text: '#1F2937',
      accent: '#CBD5E1'
    }
  },
  // Bold & Rich
  {
    id: 'forest-amber',
    name: 'Forest & Amber',
    category: 'bold',
    backgroundSwatch: 'pure-white',
    colors: {
      primary: '#059669',
      secondary: '#F59E0B',
      background: '#FFFFFF',
      text: '#1F2937',
      accent: '#DC2626'
    }
  },
  {
    id: 'midnight-gold',
    name: 'Midnight & Gold',
    category: 'bold',
    backgroundSwatch: 'cool-white',
    colors: {
      primary: '#1E40AF',
      secondary: '#F59E0B',
      background: '#F8F9FA',
      text: '#1F2937',
      accent: '#8B5CF6'
    }
  },
  {
    id: 'warm-earth',
    name: 'Warm Earth',
    category: 'bold',
    backgroundSwatch: 'warm-cream',
    colors: {
      primary: '#8B5E3C',
      secondary: '#D4A574',
      background: '#FAF7F2',
      text: '#1F2937',
      accent: '#E8C4A0'
    }
  },
  {
    id: 'sunrise',
    name: 'Sunrise',
    category: 'bold',
    backgroundSwatch: 'warm-cream',
    colors: {
      primary: '#EA580C',
      secondary: '#FCD34D',
      background: '#FAF7F2',
      text: '#1F2937',
      accent: '#FDE68A'
    }
  },
  {
    id: 'tropical',
    name: 'Tropical',
    category: 'bold',
    backgroundSwatch: 'soft-white',
    colors: {
      primary: '#0D9488',
      secondary: '#F59E0B',
      background: '#FAFAFA',
      text: '#1F2937',
      accent: '#99F6E4'
    }
  },
  // Dark & Moody
  {
    id: 'celestial',
    name: 'Celestial',
    category: 'dark',
    backgroundSwatch: 'slate',
    colors: {
      primary: '#7B68EE',
      secondary: '#C4B5FD',
      background: '#2D3748',
      text: '#FFFFFF',
      accent: '#4A3F8A'
    }
  },
  {
    id: 'deep-ocean',
    name: 'Deep Ocean',
    category: 'dark',
    backgroundSwatch: 'near-black',
    colors: {
      primary: '#1E3A5F',
      secondary: '#64B5F6',
      background: '#111827',
      text: '#FFFFFF',
      accent: '#90CAF9'
    }
  }
];

const ColorSchemePanel = ({ onNavigate }) => {
  const { state, actions } = useWebsiteBuilder();
  const { draft } = state;
  const [showPaletteHelper, setShowPaletteHelper] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({ light: false, bold: false, dark: false });

  const toggleCategory = (cat) => setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  const isCustom = draft.design.colorScheme === 'custom';
  const currentColors = draft.design.customColors || COLOR_PRESETS[0].colors;
  const currentBackgroundSwatch = draft.design.backgroundSwatch || 'pure-white';

  const handlePresetSelect = (preset) => {
    actions.updateDesign('colorScheme', preset.id);
    actions.updateDesign('customColors', preset.colors);
    actions.updateDesign('backgroundSwatch', preset.backgroundSwatch);
  };

  const handleCustomColorChange = (field, value) => {
    const updatedColors = { ...currentColors, [field]: value };
    actions.updateDesign('colorScheme', 'custom');
    actions.updateDesign('customColors', updatedColors);
  };

  const handleBackgroundSwatchSelect = (swatchId) => {
    // Find the swatch color
    const allSwatches = [...BACKGROUND_SWATCHES.light, ...BACKGROUND_SWATCHES.dark];
    const swatch = allSwatches.find(s => s.id === swatchId);

    if (swatch) {
      const bgColor = swatch.color;
      // Simplified text color: all light → dark, all dark → white
      const isLightBackground = BACKGROUND_SWATCHES.light.some(s => s.id === swatchId);
      const textColor = isLightBackground ? '#1F2937' : '#FFFFFF';
      const primaryColor = currentColors.primary || '#7C3AED';
      const accentColor = primaryColor + '26'; // 15% opacity

      // Update background and auto-calculate text/accent
      const updatedColors = {
        ...currentColors,
        background: bgColor,
        text: textColor,
        accent: accentColor
      };

      actions.updateDesign('colorScheme', 'custom');
      actions.updateDesign('customColors', updatedColors);
      actions.updateDesign('backgroundSwatch', swatchId);
    }
  };

  const handlePaletteSelect = (colors) => {
    // ColorPaletteHelper passes an object {primary, secondary, background}
    // Auto-calculate text color based on background luminance
    const bgColor = colors.background || '#FFFFFF';
    const textColor = getTextColor(bgColor);
    const secondaryTextColor = getSecondaryTextColor(bgColor);

    // Update to custom scheme with palette colors
    actions.updateDesign('colorScheme', 'custom');
    actions.updateDesign('customColors', {
      primary: colors.primary || '#7C3AED',
      secondary: colors.secondary || colors.primary || '#FCD34D',
      background: bgColor,
      text: textColor,
      secondaryText: secondaryTextColor,
      accent: colors.accent || colors.secondary || colors.primary || '#EC4899'
    });
    setShowPaletteHelper(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Color Scheme</h3>
        <p className="text-sm text-slate-600">Choose a preset or create your own palette</p>
      </div>

      {/* Hero image hint */}
      <div style={{ marginTop: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px', padding: '10px 12px' }}>
        <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
          Want an image or gradient instead?{' '}
          <span
            role="button"
            tabIndex={0}
            onClick={() => onNavigate && onNavigate('background')}
            onKeyDown={(e) => { if (e.key === 'Enter') onNavigate && onNavigate('background'); }}
            style={{
              color: '#7c3aed',
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'color 0.15s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#6D28D9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#7c3aed'; }}
          >
            Set it in Hero Background →
          </span>
        </p>
      </div>

      {/* Preset Color Schemes - Collapsible Categories */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Preset Schemes</label>
        <div className="space-y-1">
          {[
            { key: 'light', label: 'Light & Airy' },
            { key: 'bold',  label: 'Bold & Rich' },
            { key: 'dark',  label: 'Dark & Moody' },
          ].map(({ key, label }) => (
            <div key={key}>
              {/* Accordion header */}
              <span
                role="button"
                tabIndex={0}
                onClick={() => toggleCategory(key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCategory(key); } }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#6B7280',
                  cursor: 'pointer',
                  padding: '8px 0',
                  userSelect: 'none',
                  transition: 'color 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#4B5563'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7280'; }}
              >
                {label}
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ transform: expandedCategories[key] ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', flexShrink: 0 }}
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </span>

              {/* Collapsible presets */}
              <div style={{ overflow: 'hidden', maxHeight: expandedCategories[key] ? '1000px' : '0', transition: 'max-height 0.3s ease' }}>
                <div className="space-y-2 pb-3">
                  {COLOR_PRESETS.filter(p => p.category === key).map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => handlePresetSelect(preset)}
                      className={`w-full p-3 rounded-lg border-2 transition-all ${
                        draft.design.colorScheme === preset.id && !isCustom
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-900">{preset.name}</span>
                        {draft.design.colorScheme === preset.id && !isCustom && (
                          <Check className="w-5 h-5 text-purple-600" />
                        )}
                      </div>
                      <div className="flex gap-2">
                        {Object.values(preset.colors).map((color, idx) => (
                          <div
                            key={idx}
                            className="w-8 h-8 rounded border border-slate-200"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Color Theory Helper - Prominent Feature Card */}
      <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Color Theory Helper</h4>
            <p className="text-xs text-gray-600 mb-3">
              Get AI-powered color suggestions using complementary, analogous, and triadic color theory
            </p>
            <button
              onClick={() => setShowPaletteHelper(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 bg-white hover:bg-purple-50 border border-purple-200 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Generate Palettes
            </button>
          </div>
        </div>
      </div>

      {/* Custom Colors Section */}
      <div className="pt-4 border-t border-slate-200">
        <label className="text-sm font-medium text-slate-700 mb-4 block">
          Custom Colors {isCustom && <span className="text-purple-600">(Active)</span>}
        </label>

        <div className="space-y-4">
          {/* Primary Color picker */}
          <ColorPicker
            label="Primary Color"
            helperText="Main brand color for buttons and accents"
            value={currentColors.primary}
            onChange={(color) => handleCustomColorChange('primary', color)}
            showHexInput={true}
          />

          {/* Secondary Color picker — always visible */}
          <ColorPicker
            label="Secondary Color"
            helperText="Complementary color for highlights"
            value={currentColors.secondary}
            onChange={(color) => handleCustomColorChange('secondary', color)}
            showHexInput={true}
          />

          {/* Fix 2: Secondary color helper text */}
          <p style={{ fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', marginTop: '-8px' }}>
            Used for accent borders, hover states, and secondary buttons on your site
          </p>
        </div>
      </div>

      {/* Background Mode - Curated Swatches Only */}
      <div className="pt-4 border-t border-slate-200">
        <label className="text-sm font-medium text-slate-700 mb-2 block">
          Background Mode
        </label>
        <p className="text-xs text-slate-500 mb-4">
          Choose a curated background that ensures text readability
        </p>

        {/* Light Backgrounds */}
        <div className="mb-4">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Light</div>
          <div className="flex gap-3">
            {BACKGROUND_SWATCHES.light.map((swatch) => (
              <button
                key={swatch.id}
                onClick={() => handleBackgroundSwatchSelect(swatch.id)}
                className={`relative group`}
              >
                <div
                  className={`w-10 h-10 rounded-full border-2 transition-all ${
                    currentBackgroundSwatch === swatch.id
                      ? 'border-purple-600 ring-2 ring-purple-200'
                      : 'border-slate-300 hover:border-purple-300'
                  }`}
                  style={{ backgroundColor: swatch.color }}
                />
                {currentBackgroundSwatch === swatch.id && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Check className="w-5 h-5 text-purple-600 drop-shadow-sm" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Dark Backgrounds */}
        <div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Dark</div>
          <div className="flex gap-3">
            {BACKGROUND_SWATCHES.dark.map((swatch) => (
              <button
                key={swatch.id}
                onClick={() => handleBackgroundSwatchSelect(swatch.id)}
                className={`relative group`}
              >
                <div
                  className={`w-10 h-10 rounded-full border-2 transition-all ${
                    currentBackgroundSwatch === swatch.id
                      ? 'border-purple-600 ring-2 ring-purple-200'
                      : 'border-slate-300 hover:border-purple-300'
                  }`}
                  style={{ backgroundColor: swatch.color }}
                />
                {currentBackgroundSwatch === swatch.id && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Check className="w-5 h-5 text-white drop-shadow-md" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Auto-Calculated Colors Info */}
      <div className="pt-4 border-t border-slate-200">
        <label className="text-sm font-medium text-slate-700 mb-2 block">
          Auto-Calculated Colors
        </label>
        <p className="text-xs text-slate-500 mb-4">
          These colors are automatically calculated for optimal readability
        </p>

        <div className="space-y-3">
          {/* Text Color - Read Only */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded border border-slate-300"
                style={{ backgroundColor: currentColors.text }}
              />
              <div>
                <div className="text-sm font-medium text-slate-900">Text Color</div>
                <div className="text-xs text-slate-500">Based on background contrast</div>
              </div>
            </div>
            <div className="text-xs font-mono text-slate-600">{currentColors.text}</div>
          </div>

          {/* Accent Color - Read Only */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded border border-slate-300"
                style={{ backgroundColor: currentColors.accent }}
              />
              <div>
                <div className="text-sm font-medium text-slate-900">Accent Color</div>
                <div className="text-xs text-slate-500">15% tint of primary</div>
              </div>
            </div>
            <div className="text-xs font-mono text-slate-600">{currentColors.accent}</div>
          </div>
        </div>
      </div>

      {/* Color Palette Helper Modal */}
      {showPaletteHelper && (
        <ColorPaletteHelper
          isOpen={showPaletteHelper}
          onClose={() => setShowPaletteHelper(false)}
          onSelectPalette={handlePaletteSelect}
          currentTemplate={draft.design.layoutStyle}
          startingColors={[
            { color: currentColors.primary, name: 'Current Primary', vibe: 'Your brand color' },
            { color: '#7C3AED', name: 'Purple', vibe: 'Mystical & spiritual' },
            { color: '#10B981', name: 'Green', vibe: 'Healing & growth' },
            { color: '#0EA5E9', name: 'Blue', vibe: 'Calm & trustworthy' },
            { color: '#F59E0B', name: 'Gold', vibe: 'Warm & inviting' },
            { color: '#EC4899', name: 'Pink', vibe: 'Loving & compassionate' },
            { color: '#0D9488', name: 'Teal', vibe: 'Balance & clarity' },
            { color: '#DC2626', name: 'Red', vibe: 'Passion & energy' },
            { color: '#EA580C', name: 'Orange', vibe: 'Creative & warm' },
            { color: '#4F46E5', name: 'Indigo', vibe: 'Intuitive & deep' },
            { color: '#92400E', name: 'Earth Brown', vibe: 'Grounded & natural' },
            { color: '#FB7185', name: 'Coral', vibe: 'Gentle & welcoming' }
          ]}
        />
      )}
    </div>
  );
};

export default ColorSchemePanel;
